import React from "react"
import { useTranslation } from "react-i18next"

import { useAlert, useForm, useOperator, useUser } from "hooks"
import * as BikeApi from "features/bike/api/BikeApi"

import { Button, CopyToClipboard, Spinner } from "components/Elements"
import { FormElement, formStatusEnum, lodashSetFormFields } from "components/Form"
import {
	bikeBrands,
	bikeStatusList,
	bikeTypes,
	BIKE_STATUS_BACK_IN_STORE,
	sanitizeCode,
} from "utils"
import { NeedHelpBlock } from "../NeedHelpBlock"
import { BikeRegistrationActionType, useBikeRegistrationProcess } from "features/dashboard/hooks"
import { ErrorResponse } from "models"
import { BICYCODE_ASSO_EMAIL, BICYCODE_ID } from "operators/bicycode/config"

const defaultFormFields = {
	passport_code: "",
	bike_description: {
		brand: "",
		brand_id: "",
		model: "",
		gear_type: "",
		gear_type_id: "",
		color: "",
		frame_serial_number: "",
		is_electric: "",
	},
	bike_status: "",
}

const OTHER_BRAND_VALUE = 999
const OTHER_TYPE_VALUE = 999

export const RegisterBikeForm: React.FC = () => {
	const { bikeRegistrationDispatch, bikeRegistrationState } = useBikeRegistrationProcess()

	const bikeToSyncWithFnuci = bikeRegistrationState?.bike
	const bikeShouldBeSyncWithFnuci = !!bikeToSyncWithFnuci
	const bikeShouldBeRegister = !bikeShouldBeSyncWithFnuci

	const {
		formFields,
		setFormFields,
		formStatus,
		setFormStatus,
		handleChange,
		errors,
		showLocalErrors,
		setShowLocalErrors,
		handleValidation,
	} = useForm({
		defaultFormFields: bikeToSyncWithFnuci ?? defaultFormFields,
		defaultFormStatus: formStatusEnum.ready,
	})

	const { user } = useUser()
	const { alert } = useAlert()
	const { t } = useTranslation()
	const { currentOperator } = useOperator()

	const isLoading = formStatus === formStatusEnum.loading
	const isReady = formStatus === formStatusEnum.ready

	const [isShowMakerInfo, setIsShowMakerInfo] = React.useState<boolean>(false)
	const [isShowCodeInfo, setIsShowCodeInfo] = React.useState<boolean>(false)

	const statusOptions = bikeStatusList
		.filter((bikeStatus) => bikeStatus.value !== BIKE_STATUS_BACK_IN_STORE)
		.map((bikeStatus) => ({
			label: bikeStatus.label,
			value: bikeStatus.value,
		}))

	const handleSubmit = async (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault()
		// Validate inputs
		const formReady = !(errors.length > 0)

		if (!formReady) {
			setShowLocalErrors(true)
		} else {
			try {
				// hide error
				setShowLocalErrors(false)
				// prevent form to be submitted again
				setFormStatus(formStatusEnum.loading)

				const formFieldsBikeDescription = formFields.bike_description

				const bikeDescription = {
					brand: formFieldsBikeDescription.brand,
					brand_id: formFieldsBikeDescription.brand_id,
					model: formFieldsBikeDescription.model,
					gear_type: formFieldsBikeDescription.gear_type,
					gear_type_id: formFieldsBikeDescription.gear_type_id,
					color: formFieldsBikeDescription.color,
					is_electric: formFieldsBikeDescription.is_electric === "true" ? true : false,
					...(formFieldsBikeDescription.frame_serial_number
						? { frame_serial_number: formFieldsBikeDescription.frame_serial_number }
						: {}),
				}

				const bikePayload = {
					bike_id: bikeRegistrationState?.bikeId,
					bike_description: bikeDescription,
					bike_owner: user.description,
					bike_status: formFields.bike_status,
					bike_registered_by: formFields.bike_registered_by,
				}

				// We don't call the same API Routes if we update or register the bike
				if (bikeToSyncWithFnuci) {
					// Update bike
					const registeredBikePayload = {
						...bikePayload,
						row_version: formFields.row_version,
					}
					const { getError } = await BikeApi.updateBike(registeredBikePayload)
					if (getError()) throw getError()
				} else {
					// Register bike === Migrate Bicycode
					const registeredBikePayload = {
						...bikePayload,
						password: sanitizeCode(formFields.passport_code),
					}

					const { getError } = await BikeApi.migrateBicycodeBike(registeredBikePayload)
					if (getError()) throw getError()
				}

				// If no error, we update the context to inital
				bikeRegistrationDispatch({
					type: BikeRegistrationActionType.INITIAL_STEP,
				})
			} catch (error) {
				console.log(error)
				const errorMsg = (error as ErrorResponse)?.message?.toString()
				alert("error", errorMsg)
			} finally {
				setFormStatus(formStatusEnum.ready)
			}
		}
	}

	const formElementCommonProps = {
		showErrors: showLocalErrors,
		onValidate: handleValidation,
		onChange: handleChange,
	}

	return (
		<form className="max-w-lg mx-auto">
			{bikeShouldBeRegister && (
				// Passport code field
				<>
					<FormElement
						{...formElementCommonProps}
						value={sanitizeCode(formFields.passport_code)}
						name="passport_code"
						type="text"
						id="passport_code"
						label={t("add_bike_page.passport_code", {
							ns: "bike",
						})}
						className="mb-4"
						classNameLabel="text-primary font-bold mb-4"
						required={true}
					/>

					<div className="flex justify-end">
						<button
							type="button"
							onClick={() =>
								setIsShowCodeInfo((prevIsShowCodeInfo) => !prevIsShowCodeInfo)
							}
							className="underline text-xxs mb-6 text-primary text-center"
						>
							{t("add_bike_page.resend_code", {
								ns: "bike",
							})}
						</button>
					</div>

					{isShowCodeInfo && (
						<p className="px-4 mb-6 text-xs text-center">
							{t("add_bike_page.support_contact", {
								ns: "bike",
							})}
							<br />
							{currentOperator.id === BICYCODE_ID && (
								<CopyToClipboard
									text={BICYCODE_ASSO_EMAIL}
									className="font-bold pl-1 text-primary"
								/>
							)}
						</p>
					)}
				</>
			)}

			<h2 className="c-h2 text-primary pb-1">
				{t("add_bike_page.bike_description", { ns: "bike" })}
			</h2>

			<FormElement
				{...formElementCommonProps}
				value={formFields?.bike_description.brand_id}
				name="bike_description.brand_id"
				id="brand"
				type="select"
				options={bikeBrands}
				label={t("description.brand", { ns: "bike" })}
				className="my-1 px-1"
				required={true}
				showErrors={showLocalErrors}
				onChange={(event: React.ChangeEvent<HTMLOptionElement>) => {
					const value = event.target.value
					const label = event.target.label
					lodashSetFormFields(
						{
							"bike_description.brand_id": value,
							"bike_description.brand":
								parseInt(value) === OTHER_BRAND_VALUE ? "" : label,
						},
						setFormFields
					)
				}}
			/>

			{formFields.bike_description.brand_id === OTHER_BRAND_VALUE && (
				<FormElement
					{...formElementCommonProps}
					value={formFields.bike_description.brand}
					name="bike_description.brand"
					type="text"
					label={t("description.other_brand", {
						ns: "bike",
					})}
					required={true}
				/>
			)}

			<FormElement
				{...formElementCommonProps}
				value={formFields.bike_description.model}
				name="bike_description.model"
				id="model"
				type="text"
				label={t("description.model", { ns: "bike" })}
				className="my-1 px-1"
				required={true}
			/>

			<FormElement
				{...formElementCommonProps}
				value={formFields.bike_description.gear_type_id}
				name="bike_description.gear_type_id"
				id="brand"
				type="select"
				options={bikeTypes}
				label={t("description.type", { ns: "bike" })}
				className="my-1 px-1"
				required={true}
				onChange={(event: React.ChangeEvent<HTMLOptionElement>) => {
					const value = event.target.value
					const label = event.target.label
					lodashSetFormFields(
						{
							"bike_description.gear_type_id": value,
							"bike_description.gear_type":
								parseInt(value) === OTHER_BRAND_VALUE ? "" : label,
						},
						setFormFields
					)
				}}
			/>

			{formFields.bike_description.gear_type_id === OTHER_TYPE_VALUE && (
				<FormElement
					{...formElementCommonProps}
					value={formFields.bike_description.gear_type}
					name="bike_description.gear_type"
					type="text"
					label={t("description.other_type", {
						ns: "bike",
					})}
					required={true}
				/>
			)}

			<FormElement
				{...formElementCommonProps}
				value={formFields.bike_description.color}
				name="bike_description.color"
				id="color"
				type="text"
				label={t("description.color", { ns: "bike" })}
				className="my-1 px-1"
				required={true}
			/>

			<div className="relative">
				<FormElement
					{...formElementCommonProps}
					value={formFields.bike_description.serial_number}
					name="bike_description.serial_number"
					id="serial_number"
					type="text"
					label={t("add_bike_page.serial_number", {
						ns: "bike",
					})}
					className="my-1 px-1"
				/>
				<button
					type="button"
					onClick={() =>
						setIsShowMakerInfo((prevIsShowMakerInfo) => !prevIsShowMakerInfo)
					}
					className="absolute right-5 top-8 bg-primary rounded-full text-white px-2"
					aria-label={t("add_bike_page.maker_button", {
						ns: "bike",
					})}
					data-tip={t("add_bike_page.maker_button", {
						ns: "bike",
					})}
				>
					?
				</button>
				{isShowMakerInfo && (
					<p className="px-4 mb-2 text-xs text-primary">
						{t("add_bike_page.maker_information", {
							ns: "bike",
						})}
					</p>
				)}
			</div>

			<FormElement
				{...formElementCommonProps}
				value={formFields.bike_description.is_electric}
				name="bike_description.is_electric"
				type="radio"
				options={[
					{
						id: "vae-true",
						value: "true",
						label: t("description.electric", {
							ns: "bike",
						}),
					},
					{
						id: "vae-false",
						value: "false",
						label: t("description.mecanic", {
							ns: "bike",
						}),
					},
				]}
				classNameOptionsContainer="flex"
				classNameOption="ml-2"
				className="mb-3"
				required={true}
			/>

			<p className="text-center text-danger py-4 md:p-4">
				{t("add_bike_page.fnuci_message", { ns: "bike" })}
			</p>

			<FormElement
				{...formElementCommonProps}
				value={formFields.bike_status}
				name="bike_status"
				id="bike_status"
				type="select"
				options={statusOptions}
				label={t("bike_state.title", { ns: "bike" })}
				className="my-1 px-1"
				required={true}
			/>

			{isReady && (
				<div className="flex justify-center pt-8">
					<Button
						onClick={handleSubmit}
						className="mx-1 md:mx-2 px-5 md:px-12 py-1"
						variant="secondary"
					>
						{bikeToSyncWithFnuci ? t("global.update") : t("global.add")}
					</Button>
				</div>
			)}

			{isLoading && (
				<div className="flex justify-center my-8">
					<Spinner color={"var(--primaryColor)"} size="100px" />
				</div>
			)}

			<NeedHelpBlock className="mt-12" />
		</form>
	)
}
